/**
 * AI CONTEXT
 * Purpose: main state/context engine for evaluation table page (data joins, filters, search, pagination).
 * Inputs: classes/evals/traits subscriptions + students/users maps + user settings.
 * Exposes: classesForTable, evaluation filters, options, lookup maps, readiness.
 * Used by: EvaluationView and child components.
 * Refactor: split into smaller hooks (data loading, filtering, persistence) to reduce file size.
 */
import { compact, isEmpty, isEqual, keyBy, uniq } from "lodash"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  classAtoms,
  classSessionAtoms,
  evalTraitAtoms,
  evaluationAtoms,
} from "#atoms"
import { Student } from "#classes/Student"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  EvaluationProblemFlag,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { UserSettings, Uuid } from "#customTypes/globalTypes"
import { DataWithId } from "#customTypes/types"
import { useIsAllFulfilled } from "#hooks/jotai/useCascadingData"
import { useGetAllStudents } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { useGetAllUsers, useGetTeachers } from "#hooks/newHooks/useGetUser"
import useOptimizedSearchWithPagination from "#hooks/newHooks/useOptimizedSearchWithPagination"
import {
  generateEvalId,
  getClampedEvalSemester,
} from "#utils/evaluationHelpers"
import { ErrorType } from "#utils/textFormatters"

import { EvaluationFormContextProvider } from "./useEvaluationFormContext"
import useEvaluationProblemFlags from "./useEvaluationProblemFlags"

// Type for the searchable class data
interface ClassForEvaluationTable extends DataWithId {
  id: string
  name: string
  studentNames: string[]
  teacherNames: string[]
  classObj: ClassConstructor
}

interface EvaluationViewContextType {
  studentsById: Record<Uuid, StudentConstructor>
  classesById: Record<Uuid, ClassConstructor>
  classSessionsById: Record<Uuid, ClassSessionConstructor>
  usersById: Record<Uuid, UserConstructor>
  evaluationsById: Record<Uuid, EvaluationConstructor>
  traitsById: Record<Uuid, EvaluationTrait>
  teacherOptions: DropdownOption[]
  approverOptions: DropdownOption[]

  isReady: boolean
  isDashboardReady: boolean
  errors: ErrorType[] | null

  // Table data functionality
  classesForTable: ClassForEvaluationTable[]
  classesMatchingSearch: ClassForEvaluationTable[]
  onSearch: (searchTerm: string) => void
  searchTerm: string
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalResults: number

  // Evaluation filters
  statusFilters: string[]
  teacherFilters: string[]
  approverFilters: string[]
  setStatusFilters: (statuses: string[]) => void
  setTeacherFilters: (userIds: string[]) => void
  setApproverFilters: (userIds: string[]) => void
  onClearFilters: () => Partial<UserSettings>
  availableTeacherOptions: DropdownOption[]
  availableApproverOptions: DropdownOption[]
  filteredStudentIdsByClassId: Record<Uuid, Uuid[]>
  sortByStatus: boolean
  toggleSortByStatus: () => void
  problemFlagsByEvaluationId: Record<Uuid, EvaluationProblemFlag>
}

const EvaluationViewContext = createContext<EvaluationViewContextType | null>(
  null
)

const DELAY_MS = 300

const SEARCHABLE_FIELDS: (keyof ClassForEvaluationTable)[] = [
  "name",
  "studentNames",
  "teacherNames",
] as const

export const EvaluationViewContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { getUserSetting, setUserSetting } = useUserContext()
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.EVALUATIONS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.EVALUATIONS_TABLE_SEARCH_TERM
  )
  const defaultPage = getUserSetting<number>(
    USER_SETTINGS.EVALUATIONS_TABLE_PAGE
  )
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.EVALUATIONS_TABLE_SEMESTER
  )
  const sortByStatus = getUserSetting<boolean>(
    USER_SETTINGS.EVALUATIONS_TABLE_SORT_BY_STATUS
  )

  // DB values from Firestore listener
  const dbStatusFilters = getUserSetting<string[]>(
    USER_SETTINGS.EVALUATIONS_TABLE_STATUS_FILTER
  )
  const dbTeacherFilters = getUserSetting<string[]>(
    USER_SETTINGS.EVALUATIONS_TABLE_TEACHER_FILTER
  )
  const dbApproverFilters = getUserSetting<string[]>(
    USER_SETTINGS.EVALUATIONS_TABLE_APPROVER_FILTER
  )

  // Local state for immediate UI response; DB writes are debounced
  const [statusFilters, setLocalStatusFilters] =
    useState<string[]>(dbStatusFilters)
  const [teacherFilters, setLocalTeacherFilters] =
    useState<string[]>(dbTeacherFilters)
  const [approverFilters, setLocalApproverFilters] =
    useState<string[]>(dbApproverFilters)
  const statusDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const teacherDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const approverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync from DB -> local when no pending debounced write
  useEffect(() => {
    if (!statusDebounceRef.current) {
      setLocalStatusFilters(dbStatusFilters)
    }
  }, [dbStatusFilters])

  useEffect(() => {
    if (!teacherDebounceRef.current) {
      setLocalTeacherFilters(dbTeacherFilters)
    }
  }, [dbTeacherFilters])

  useEffect(() => {
    if (!approverDebounceRef.current) {
      setLocalApproverFilters(dbApproverFilters)
    }
  }, [dbApproverFilters])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (statusDebounceRef.current) clearTimeout(statusDebounceRef.current)
      if (teacherDebounceRef.current) clearTimeout(teacherDebounceRef.current)
      if (approverDebounceRef.current) clearTimeout(approverDebounceRef.current)
    }
  }, [])

  const hasShownRef = useRef(false)

  // First, fetch all classes for the semester
  const semesterQuery = useMemo(() => {
    return {
      queries: {
        semester: {
          value: filterSemester,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [filterSemester])

  const classes = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    semesterQuery
  )

  const isClassesFulfilled = !!classes.length

  const evaluations = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    semesterQuery
  )
  const classSessions = useFilteredDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    semesterQuery
  )
  const hasAnySessionIds = useMemo(
    () => classes.some((classObj) => (classObj.sessionIds || []).length > 0),
    [classes]
  )
  const isClassSessionsFulfilled = !hasAnySessionIds || !!classSessions.length

  const clampedSemester = getClampedEvalSemester(filterSemester)
  const evalTraitsQuery = useMemo(() => {
    return {
      queries: {
        semesters: {
          value: clampedSemester,
          comparator: COMPARATORS.CONTAINS,
        },
      },
    }
  }, [clampedSemester])

  const evalTraits = useFilteredDocs<EvaluationTrait>(
    evalTraitAtoms,
    DOC_TYPES.EVAL_TRAITS,
    evalTraitsQuery
  )

  const isEvalTraitsFulfilled = !!evalTraits.length

  const students = useGetAllStudents()
  const users = useGetAllUsers()
  const teachers = useGetTeachers()
  const teacherOptions = useMemo(() => {
    return teachers
      .map((teacher) => ({
        key: teacher.id,
        label: teacher.name,
        value: teacher.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [teachers])

  const approverOptions = useMemo(() => {
    return users
      .filter((u) => u.role !== USER_ROLES.DISABLED)
      .map((user) => ({
        key: user.id,
        label: user.name,
        value: user.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [users])

  const classesById = useMemo(() => keyBy(classes, "id"), [classes])
  const classSessionsById = useMemo(
    () => keyBy(classSessions, "id"),
    [classSessions]
  )
  const evaluationsById = useMemo(() => keyBy(evaluations, "id"), [evaluations])
  const studentsById = useMemo(() => keyBy(students, "id"), [students])
  const usersById = useMemo(() => keyBy(users, "id"), [users])
  const traitsById = useMemo(() => keyBy(evalTraits, "id"), [evalTraits])
  const previousClassesForTableByIdRef = useRef<
    Record<Uuid, ClassForEvaluationTable>
  >({})

  // Track fulfillment
  const allFulfilled = useIsAllFulfilled([
    isClassesFulfilled,
    // isEvaluationsFulfilled,
    isEvalTraitsFulfilled,
  ])
  const isDashboardReady = useIsAllFulfilled([
    isClassesFulfilled,
    isClassSessionsFulfilled,
  ])

  // Once shown, keep showing
  if (allFulfilled) {
    hasShownRef.current = true
  }

  const nonCampClasses = useMemo(
    () => classes.filter((c) => !c.isSummerCamp),
    [classes]
  )

  // Transform classes into searchable format with sorting
  const allClassesForTable = useMemo(() => {
    if (!allFulfilled || isEmpty(studentsById) || isEmpty(usersById)) return []

    const classesArray = nonCampClasses.map((classObj) => {
      // Get all unique student names from class.studentIds
      const studentNames = uniq(classObj.studentIds || [])
        .map((studentId) =>
          new Student(studentsById[studentId]).getNameAndDob()
        )
        .filter(Boolean)

      // Get all teacher names assigned to this class
      const teacherNames = (classObj.teacherIds || [])
        .map((teacherId) => usersById[teacherId]?.name)
        .filter(Boolean)

      // Memoize with ref to cut down on re-renders
      const classId = classObj.id
      const previousValues = previousClassesForTableByIdRef.current[classId]
      const isSame =
        previousValues &&
        isEqual(previousValues, { studentNames, teacherNames }) &&
        previousValues.classObj.name === classObj.name

      if (isSame) {
        return previousValues
      }

      previousClassesForTableByIdRef.current[classId] = {
        id: classObj.id,
        name: classObj.name,
        studentNames,
        teacherNames,
        classObj,
      }

      return previousClassesForTableByIdRef.current[classId]
    })

    // Sort classes: non-empty classes first (sorted by name), then empty classes (sorted by name)
    return classesArray.sort((a, b) => {
      const aIsTaClass = a.name.includes("TA")
      const bIsTaClass = b.name.includes("TA")

      if (aIsTaClass && !bIsTaClass) {
        return 1
      }

      if (!aIsTaClass && bIsTaClass) {
        return -1
      }

      const aHasStudents = a.studentNames.length > 0
      const bHasStudents = b.studentNames.length > 0

      if (aHasStudents && !bHasStudents) {
        return -1
      }

      if (!aHasStudents && bHasStudents) {
        return 1
      }

      // If both have the same student status, sort alphabetically by name
      return a.classObj.name.localeCompare(b.classObj.name)
    })
  }, [nonCampClasses, studentsById, usersById, allFulfilled])

  const availableTeacherOptions = useMemo(() => {
    const userIdSet = new Set<Uuid>()
    for (const evaluation of evaluations) {
      if (evaluation.assignedTo) userIdSet.add(evaluation.assignedTo)
    }
    return compact(
      Array.from(userIdSet).map((userId) => {
        const user = usersById[userId]
        if (!user) return null
        return { key: userId, label: user.name, value: userId }
      })
    ).sort((a, b) => a.label.localeCompare(b.label))
  }, [evaluations, usersById])

  const availableApproverOptions = useMemo(() => {
    const userIdSet = new Set<Uuid>()
    for (const evaluation of evaluations) {
      const approverFilterId = evaluation.approverId ?? evaluation.approvedBy
      if (approverFilterId) userIdSet.add(approverFilterId)
    }
    return compact(
      Array.from(userIdSet).map((userId) => {
        const user = usersById[userId]
        if (!user) return null
        return { key: userId, label: user.name, value: userId }
      })
    ).sort((a, b) => a.label.localeCompare(b.label))
  }, [evaluations, usersById])

  const hasStatusFilter = statusFilters.length > 0
  const hasTeacherFilter = teacherFilters.length > 0
  const hasApproverFilter = approverFilters.length > 0
  const hasAnyFilter = hasStatusFilter || hasTeacherFilter || hasApproverFilter

  // Compute which student IDs pass the evaluation filters for each class
  const filteredStudentIdsByClassId = useMemo(() => {
    if (!hasAnyFilter) return {}

    const result: Record<Uuid, Uuid[]> = {}
    const statusSet = new Set(statusFilters)
    const teacherSet = new Set(teacherFilters)
    const approverSet = new Set(approverFilters)

    for (const classObj of nonCampClasses) {
      const passing: Uuid[] = []
      for (const studentId of uniq(classObj.studentIds || [])) {
        const evalId = generateEvalId(
          studentId,
          classObj.semester,
          classObj.group
        )
        const evaluation = evaluationsById[evalId]

        const passesStatus =
          !hasStatusFilter ||
          (evaluation && statusSet.has(evaluation.status)) ||
          (!evaluation && statusSet.has(EVALUATION_STATUS.NOT_STARTED))

        const passesTeacher =
          !hasTeacherFilter ||
          (evaluation?.assignedTo && teacherSet.has(evaluation.assignedTo))

        const approverFilterId =
          evaluation?.approverId ?? evaluation?.approvedBy
        const passesApprover =
          !hasApproverFilter ||
          (approverFilterId ? approverSet.has(approverFilterId) : false)

        if (passesStatus && passesTeacher && passesApprover) {
          passing.push(studentId)
        }
      }
      result[classObj.id] = passing
    }
    return result
  }, [
    nonCampClasses,
    evaluationsById,
    statusFilters,
    teacherFilters,
    approverFilters,
    hasAnyFilter,
    hasStatusFilter,
    hasTeacherFilter,
    hasApproverFilter,
  ])

  // Apply evaluation filters to class list before search/pagination
  const filteredClassesForTable = useMemo(() => {
    if (!hasAnyFilter) return allClassesForTable

    return allClassesForTable.filter((classItem) => {
      const filtered = filteredStudentIdsByClassId[classItem.id]
      return filtered && filtered.length > 0
    })
  }, [allClassesForTable, filteredStudentIdsByClassId, hasAnyFilter])

  const setStatusFilters = useCallback(
    (statuses: string[]) => {
      setLocalStatusFilters(statuses)
      if (statusDebounceRef.current) clearTimeout(statusDebounceRef.current)
      statusDebounceRef.current = setTimeout(() => {
        void setUserSetting(
          USER_SETTINGS.EVALUATIONS_TABLE_STATUS_FILTER as keyof UserSettings,
          statuses
        )
        statusDebounceRef.current = null
      }, DELAY_MS)
    },
    [setUserSetting]
  )

  const setTeacherFilters = useCallback(
    (userIds: string[]) => {
      setLocalTeacherFilters(userIds)
      if (teacherDebounceRef.current) clearTimeout(teacherDebounceRef.current)
      teacherDebounceRef.current = setTimeout(() => {
        void setUserSetting(
          USER_SETTINGS.EVALUATIONS_TABLE_TEACHER_FILTER as keyof UserSettings,
          userIds
        )
        teacherDebounceRef.current = null
      }, DELAY_MS)
    },
    [setUserSetting]
  )

  const setApproverFilters = useCallback(
    (userIds: string[]) => {
      setLocalApproverFilters(userIds)
      if (approverDebounceRef.current) clearTimeout(approverDebounceRef.current)
      approverDebounceRef.current = setTimeout(() => {
        void setUserSetting(
          USER_SETTINGS.EVALUATIONS_TABLE_APPROVER_FILTER as keyof UserSettings,
          userIds
        )
        approverDebounceRef.current = null
      }, DELAY_MS)
    },
    [setUserSetting]
  )

  // Use optimized search with pagination
  const {
    filteredItems: classesForTable,
    allFilteredItems: classesMatchingSearch,
    onSearch,
    searchTerm,
    currentPage,
    totalPages,
    onPageChange: setPage,
    totalResults,
  } = useOptimizedSearchWithPagination<ClassForEvaluationTable>(
    filteredClassesForTable,
    SEARCHABLE_FIELDS,
    resultsCount,
    defaultSearchTerm,
    defaultPage
  )

  const onPageChange = useCallback(
    (page: number) => {
      setPage(page)
      void setUserSetting(
        USER_SETTINGS.EVALUATIONS_TABLE_PAGE as keyof UserSettings,
        page
      )
    },
    [setPage, setUserSetting]
  )

  const toggleSortByStatus = useCallback(() => {
    void setUserSetting(
      USER_SETTINGS.EVALUATIONS_TABLE_SORT_BY_STATUS as keyof UserSettings,
      !sortByStatus
    )
  }, [setUserSetting, sortByStatus])

  const onClearFilters = useCallback(() => {
    if (statusDebounceRef.current) {
      clearTimeout(statusDebounceRef.current)
      statusDebounceRef.current = null
    }
    if (teacherDebounceRef.current) {
      clearTimeout(teacherDebounceRef.current)
      teacherDebounceRef.current = null
    }
    if (approverDebounceRef.current) {
      clearTimeout(approverDebounceRef.current)
      approverDebounceRef.current = null
    }
    setLocalStatusFilters([])
    setLocalTeacherFilters([])
    setLocalApproverFilters([])
    setPage(1)
    return {
      evaluationsTableStatusFilter: [] as string[],
      evaluationsTableTeacherFilter: [] as string[],
      evaluationsTableApproverFilter: [] as string[],
      evaluationsTablePage: 1,
    }
  }, [setPage])

  const isReady = hasShownRef.current
  const errors: ErrorType[] = [] // Errors are handled at the subscription level
  const { flagsByEvaluationId } = useEvaluationProblemFlags({
    evaluations,
  })

  return (
    <EvaluationViewContext.Provider
      value={{
        classesById,
        classSessionsById,
        evaluationsById,
        studentsById,
        usersById,
        traitsById,
        teacherOptions,
        approverOptions,

        isReady,
        isDashboardReady,
        errors,

        // Table data functionality
        classesForTable,
        classesMatchingSearch,
        onSearch,
        searchTerm,
        currentPage,
        totalPages,
        onPageChange,
        totalResults,

        // Evaluation filters
        statusFilters,
        teacherFilters,
        approverFilters,
        setStatusFilters,
        setTeacherFilters,
        setApproverFilters,
        onClearFilters,
        availableTeacherOptions,
        availableApproverOptions,
        filteredStudentIdsByClassId,
        sortByStatus,
        toggleSortByStatus,
        problemFlagsByEvaluationId: flagsByEvaluationId,
      }}
    >
      <EvaluationFormContextProvider>{children}</EvaluationFormContextProvider>
    </EvaluationViewContext.Provider>
  )
}

// Uses a getter to fetch stable data to prevent re-renders
const useEvaluationViewContext = <T,>(
  callback: (state: EvaluationViewContextType) => T
) => {
  const context = useContext(EvaluationViewContext)
  if (!context) {
    throw new Error(
      "useEvaluationViewContext must be used within a EvaluationViewContextProvider"
    )
  }
  return callback(context)
}

export default useEvaluationViewContext
